import { useEffect, useState } from 'react';
import { api } from '@/services/api';
import { Raffle, RaffleStatus, PrizeItem, RaffleCategory, AdminStats } from '@/types';
import { Plus, Play, Trophy, XCircle, Users, DollarSign, Calendar, Upload, X, Trash2, TrendingUp, BarChart2, Ticket } from 'lucide-react';
import { toast } from '@/store/useToast';

export function AdminPage() {
  const [raffles, setRaffles] = useState<Raffle[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    photo_url: '',
    prizes: [] as PrizeItem[],
    entry_price: 100,
    max_participants: 10,
    category: RaffleCategory.OTHER,
  });
  const [currentPrize, setCurrentPrize] = useState({ name: '', photo_url: '' });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingForPrize, setUploadingForPrize] = useState(false);

  useEffect(() => {
    loadRaffles();
    loadStats();
  }, []);

  const loadRaffles = async () => {
    try {
      setLoading(true);
      const response = await api.getAllRaffles();
      setRaffles(response);
    } catch (error) {
      console.error('Failed to load raffles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await api.getAdminStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Размер файла не должен превышать 5MB');
      return;
    }

    setSelectedFile(file);

    // Upload immediately
    try {
      setUploadingPhoto(true);
      const photoData = new FormData();
      photoData.append('file', file);

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const API_HOST = (API_URL as string).replace(/\/api\/?$/, '');
      const response = await fetch(`${API_URL}/admin/raffles/upload-photo`, {
        method: 'POST',
        body: photoData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setFormData({ ...formData, photo_url: `${API_HOST}${data.photo_url}` });
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Ошибка при загрузке фото');
      setSelectedFile(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setSelectedFile(null);
    setFormData({ ...formData, photo_url: '' });
  };

  const handlePrizePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Размер файла не должен превышать 5MB');
      return;
    }

    try {
      setUploadingForPrize(true);
      const photoData = new FormData();
      photoData.append('file', file);

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const API_HOST = (API_URL as string).replace(/\/api\/?$/, '');
      const response = await fetch(`${API_URL}/admin/raffles/upload-photo`, {
        method: 'POST',
        body: photoData,
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      setCurrentPrize({ ...currentPrize, photo_url: `${API_HOST}${data.photo_url}` });
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Ошибка при загрузке фото');
    } finally {
      setUploadingForPrize(false);
    }
  };

  const handleAddPrize = () => {
    if (!currentPrize.name.trim()) {
      toast.error('Введите название приза');
      return;
    }

    const newPrize: PrizeItem = {
      place: formData.prizes.length + 1,
      name: currentPrize.name,
      photo_url: currentPrize.photo_url || undefined,
    };

    setFormData({ ...formData, prizes: [...formData.prizes, newPrize] });
    setCurrentPrize({ name: '', photo_url: '' });
  };

  const handleRemovePrize = (index: number) => {
    const updatedPrizes = formData.prizes.filter((_, i) => i !== index)
      .map((prize, i) => ({ ...prize, place: i + 1 }));
    setFormData({ ...formData, prizes: updatedPrizes });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.createRaffle(formData);
      setShowCreateForm(false);
      setFormData({ title: '', description: '', photo_url: '', prizes: [], entry_price: 100, max_participants: 10 });
      setCurrentPrize({ name: '', photo_url: '' });
      setSelectedFile(null);
      loadRaffles();
    } catch (error) {
      console.error('Failed to create raffle:', error);
      toast.error('Ошибка при создании розыгрыша');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Удалить этот розыгрыш?')) return;
    try {
      await api.deleteRaffle(id);
      loadRaffles();
    } catch (error) {
      console.error('Failed to delete raffle:', error);
      toast.error('Ошибка при удалении розыгрыша');
    }
  };

  const handleStart = async (id: number) => {
    if (!confirm('Запустить розыгрыш?')) return;
    try {
      await api.startRaffle(id);
      loadRaffles();
    } catch (error) {
      console.error('Failed to start raffle:', error);
      toast.error('Ошибка при запуске розыгрыша');
    }
  };

  const handleDraw = async (id: number) => {
    if (!confirm('Провести розыгрыш победителя?')) return;
    try {
      await api.drawRaffle(id);
      loadRaffles();
      toast.success('Розыгрыш проведен успешно!');
    } catch (error) {
      console.error('Failed to draw raffle:', error);
      toast.error('Ошибка при проведении розыгрыша');
    }
  };

  const getStatusColor = (status: RaffleStatus) => {
    switch (status) {
      case RaffleStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case RaffleStatus.ACTIVE:
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case RaffleStatus.DRAWING:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case RaffleStatus.COMPLETED:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case RaffleStatus.CANCELLED:
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: RaffleStatus) => {
    const labels = {
      [RaffleStatus.PENDING]: 'Ожидает',
      [RaffleStatus.ACTIVE]: 'Активен',
      [RaffleStatus.DRAWING]: 'Розыгрыш',
      [RaffleStatus.COMPLETED]: 'Завершен',
      [RaffleStatus.CANCELLED]: 'Отменен',
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Админ-панель</h1>
          <p className="text-purple-100">Управление розыгрышами</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Dashboard */}
        {stats && (
          <div className="mb-6 space-y-4">
            {/* Row 1 — Users & Revenue */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Пользователей</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.users.total}</p>
                  <p className="text-xs text-green-600">+{stats.users.new_today} сегодня</p>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Выручка всего</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.revenue.total.toLocaleString('ru-RU')} ₽</p>
                  <p className="text-xs text-gray-400">{stats.revenue.month.toLocaleString('ru-RU')} ₽ за месяц</p>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Призовой фонд</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.tickets.total_pool.toLocaleString('ru-RU')} ₽</p>
                  <p className="text-xs text-gray-400">{stats.tickets.total_sold} билетов продано</p>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow flex items-center gap-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                  <Ticket className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Новых за неделю</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.users.new_week}</p>
                  <p className="text-xs text-gray-400">пользователей</p>
                </div>
              </div>
            </div>
            {/* Row 2 — Raffles by status */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow">
              <div className="flex items-center gap-2 mb-3">
                <BarChart2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Розыгрыши</span>
                <span className="ml-auto text-xs text-gray-400">всего: {stats.raffles.total}</span>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{stats.raffles.active}</p>
                  <p className="text-xs text-gray-500">Активных</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">{stats.raffles.pending}</p>
                  <p className="text-xs text-gray-500">Ожидают</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-600 dark:text-gray-300">{stats.raffles.completed}</p>
                  <p className="text-xs text-gray-500">Завершено</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-500">{stats.raffles.cancelled}</p>
                  <p className="text-xs text-gray-500">Отменено</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mb-6 flex gap-4">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Создать розыгрыш</span>
          </button>
          <button
            onClick={() => { loadRaffles(); loadStats(); }}
            className="px-6 py-3 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Обновить
          </button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 mb-6 shadow-lg">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              Новый розыгрыш
            </h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Название
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Описание
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Призы
                </label>

                {/* List of added prizes */}
                {formData.prizes.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {formData.prizes.map((prize, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <span className="font-bold text-purple-600 dark:text-purple-400">{prize.place} место:</span>
                        <span className="flex-1 text-gray-900 dark:text-white">{prize.name}</span>
                        {prize.photo_url && (
                          <img src={prize.photo_url} alt={prize.name} className="w-10 h-10 object-cover rounded" />
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemovePrize(index)}
                          className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add prize form */}
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 space-y-3">
                  <input
                    type="text"
                    value={currentPrize.name}
                    onChange={(e) => setCurrentPrize({ ...currentPrize, name: e.target.value })}
                    placeholder="Название приза (например: iPhone 15 Pro)"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />

                  {currentPrize.photo_url ? (
                    <div className="relative">
                      <img src={currentPrize.photo_url} alt="Prize preview" className="w-full h-32 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => setCurrentPrize({ ...currentPrize, photo_url: '' })}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600">
                      <Upload className="w-6 h-6 mb-1 text-gray-500 dark:text-gray-400" />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {uploadingForPrize ? 'Загрузка...' : 'Фото приза (опционально)'}
                      </p>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handlePrizePhotoSelect}
                        disabled={uploadingForPrize}
                      />
                    </label>
                  )}

                  <button
                    type="button"
                    onClick={handleAddPrize}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    + Добавить приз ({formData.prizes.length + 1} место)
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Обложка розыгрыша (опционально)
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Главное фото розыгрыша. Фото каждого приза добавляется в секции "Призы" выше.
                </p>
                {!formData.photo_url ? (
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-8 h-8 mb-2 text-gray-500 dark:text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                          {uploadingPhoto ? 'Загрузка...' : 'Нажмите для загрузки обложки'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          PNG, JPG или WEBP (макс. 5MB)
                        </p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handlePhotoSelect}
                        disabled={uploadingPhoto}
                      />
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={formData.photo_url}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Категория
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as RaffleCategory })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value={RaffleCategory.ELECTRONICS}>📱 Электроника</option>
                  <option value={RaffleCategory.TRANSPORT}>🚲 Транспорт</option>
                  <option value={RaffleCategory.GAMING}>🎮 Игровое</option>
                  <option value={RaffleCategory.MONEY}>💰 Деньги и коды</option>
                  <option value={RaffleCategory.ACCESSORIES}>👜 Аксессуары</option>
                  <option value={RaffleCategory.OTHER}>🎁 Другое</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Цена билета (₽)
                  </label>
                  <input
                    type="number"
                    value={formData.entry_price}
                    onChange={(e) => setFormData({ ...formData, entry_price: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Макс. билетов
                  </label>
                  <input
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min="2"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Создать
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-6 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Raffles List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Загрузка...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {raffles.map((raffle) => (
              <div
                key={raffle.id}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {raffle.title}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(raffle.status)}`}>
                        {getStatusLabel(raffle.status)}
                      </span>
                    </div>
                    {raffle.description && (
                      <p className="text-gray-600 dark:text-gray-400 mb-3">
                        {raffle.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Prizes */}
                {raffle.prizes && raffle.prizes.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Призы:</p>
                    <div className="space-y-1">
                      {raffle.prizes.map((prize) => (
                        <div key={prize.place} className="flex items-center gap-2 text-sm">
                          <span className="font-bold text-purple-600 dark:text-purple-400">{prize.place}.</span>
                          <span className="text-gray-900 dark:text-white">{prize.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                    <Users className="w-5 h-5" />
                    <div>
                      <p className="text-xs">Билетов</p>
                      <p className="font-semibold">{raffle.participants_count}/{raffle.max_participants || '∞'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                    <DollarSign className="w-5 h-5" />
                    <div>
                      <p className="text-xs">Цена билета</p>
                      <p className="font-semibold">{raffle.entry_price} ₽</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                    <Calendar className="w-5 h-5" />
                    <div>
                      <p className="text-xs">Создан</p>
                      <p className="font-semibold text-xs">
                        {new Date(raffle.created_at).toLocaleDateString('ru-RU')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {raffle.status === RaffleStatus.PENDING && (
                    <button
                      onClick={() => handleStart(raffle.id)}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <Play className="w-4 h-4" />
                      <span>Запустить</span>
                    </button>
                  )}
                  {raffle.status === RaffleStatus.ACTIVE && raffle.participants_count > 0 && (
                    <button
                      onClick={() => handleDraw(raffle.id)}
                      className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      <Trophy className="w-4 h-4" />
                      <span>Провести розыгрыш</span>
                    </button>
                  )}
                  {raffle.status === RaffleStatus.COMPLETED && (
                    <>
                      {raffle.winners && raffle.winners.length > 0 && (
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Победители:</p>
                          {raffle.winners.map((winner) => (
                            <div key={winner.place} className="flex items-center gap-2 text-sm">
                              <Trophy className="w-4 h-4 text-yellow-500" />
                              <span className="font-semibold">{winner.place} место:</span>
                              <span className="text-green-600 dark:text-green-400">
                                {winner.first_name || winner.username || `ID ${winner.user_id}`} - {winner.prize_name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <button
                        onClick={() => handleDelete(raffle.id)}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Удалить</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}

            {raffles.length === 0 && (
              <div className="text-center py-12">
                <XCircle className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  Розыгрышей пока нет
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
